import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { X } from "lucide-react";

const SUGGESTED_TAGS = ["microsaas", "freelance", "hackathon", "workshops", "newsletter", "notiontemplates", "pod"];

export default function CreateStrategy() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ title: "", summary: "", goal_amount: 0, tags: [] });
  const [customTag, setCustomTag] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const addTag = (t) => {
    const clean = t.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
    if (!clean || form.tags.includes(clean)) return;
    setForm((f) => ({ ...f, tags: [...f.tags, clean] }));
  };

  const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

  const toggleSuggested = (t) => {
    if (form.tags.includes(t)) removeTag(t);
    else addTag(t);
  };

  const handleCustomTagKey = (e) => {
    if ((e.key === "Enter" || e.key === ",") && customTag.trim()) {
      e.preventDefault();
      addTag(customTag);
      setCustomTag("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login"); return; }
    setErr(""); setBusy(true);
    try {
      const { data, error } = await supabase.from("strategies").insert({
        user_id:     user.id,
        title:       form.title.trim(),
        summary:     form.summary.trim(),
        goal_amount: parseFloat(form.goal_amount || 0),
        tags:        form.tags,
        methods:     [],
        cover_url:   "",
        shares:      0,
      }).select().single();
      if (error) throw error;
      nav(`/s/${data.id}`);
    } catch (e) {
      setErr(e.message || "Failed to post strategy");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <div className="font-hand text-marker-red text-3xl rotate-[-1deg] mb-1">— ship the playbook —</div>
      <h1 className="font-display font-extrabold text-5xl uppercase tracking-tight mb-6">Post a strategy</h1>
      <form onSubmit={submit} className="brutal-card p-6 space-y-4" data-testid="create-strategy-form">
        <label className="block">
          <div className="font-mono text-xs uppercase tracking-widest mb-1">Title</div>
          <input className="brutal-input" required value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="strategy-title-input" />
        </label>
        <label className="block">
          <div className="font-mono text-xs uppercase tracking-widest mb-1">Strategy details</div>
          <textarea className="brutal-input min-h-[160px]" required value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="strategy-summary-input" />
        </label>
        <label className="block">
          <div className="font-mono text-xs uppercase tracking-widest mb-1">Target amount ($)</div>
          <input className="brutal-input" type="number" min="0" value={form.goal_amount}
            onChange={(e) => setForm({ ...form, goal_amount: e.target.value })} data-testid="strategy-goal-input" />
        </label>
        <div>
          <div className="font-mono text-xs uppercase tracking-widest mb-2">Tags</div>
          <div className="font-mono text-[11px] text-ink/50 mb-2">Pick from suggestions or type your own below.</div>

          {/* Suggested tags */}
          <div className="flex flex-wrap gap-2 mb-3">
            {SUGGESTED_TAGS.map((t) => (
              <button key={t} type="button" onClick={() => toggleSuggested(t)} className="tag-pill"
                style={{ background: form.tags.includes(t) ? "#FF3333" : "#FFD600", color: form.tags.includes(t) ? "#fff" : "#121212" }}
                data-testid={`tag-toggle-${t}`}>
                #{t}
              </button>
            ))}
          </div>

          {/* Custom tag input */}
          <div className="flex gap-2 mb-3">
            <input
              className="brutal-input flex-1"
              placeholder="Type a custom tag and press Enter…"
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              onKeyDown={handleCustomTagKey}
              data-testid="custom-tag-input"
            />
            <button
              type="button"
              onClick={() => { if (customTag.trim()) { addTag(customTag); setCustomTag(""); } }}
              className="brutal-btn brutal-btn-yellow text-xs"
            >
              Add
            </button>
          </div>

          {/* Selected tags display */}
          {form.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {form.tags.map((t) => (
                <span key={t} className="tag-pill flex items-center gap-1"
                  style={{ background: "#121212", color: "#fff" }}>
                  #{t}
                  <button type="button" onClick={() => removeTag(t)} className="hover:text-marker-red">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        {err && <div className="border-2 border-marker-red bg-marker-red/10 p-3 font-mono text-sm">{err}</div>}
        <button type="submit" disabled={busy} className="brutal-btn brutal-btn-yellow w-full" data-testid="strategy-submit-btn">
          {busy ? "Posting…" : "Post strategy"}
        </button>
      </form>
    </main>
  );
}
